/**
 * Pure, DOM-free formatting helpers for the concierge UI (unit-tested).
 */

/** Format an ISO date string as a short Korean date (YYYY.MM.DD). Empty for nullish. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

/** Format an ISO timestamp as a short HH:MM clock label. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${min}`;
}

/** Truncate a string to `max` chars, appending an ellipsis when cut. */
export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1))}…`;
}

/** Format a number as Korean Won currency (e.g. ₩1,000,000). */
export function formatKrw(n: number): string {
  return `₩${n.toLocaleString("ko-KR")}`;
}

/**
 * 경과 시간(분). 값이 없거나 파싱이 안 되면 null — 호출부가 "미동기화"로 그린다.
 * 시계가 어긋나 미래 시각이 와도 음수 대신 0으로 눌러 "N분 후"라고 하지 않는다.
 */
export function minutesSince(
  iso: string | null | undefined,
  now: Date = new Date()
): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 60000));
}

/** "방금 전 / N분 전 / N시간 전 / N일 전". 값이 없으면 빈 문자열. */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: Date = new Date()
): string {
  const minutes = minutesSince(iso, now);
  if (minutes === null) return "";
  if (minutes < 1) return "방금 전";
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}
